import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Bind a native, delegated click handler. The Shopify component's
    // declarative on:click route is not reliable inside dynamically morphed
    // product sections, so keep the click target on the component itself.
    this.addEventListener('click', this.#handleStickyClick, { signal });

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  #handleStickyClick = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('.sticky-add-to-cart__button')
      : null;
    if (!button || !this.contains(button)) return;
    // Desktop: the sticky button is a real HTML submit button linked via
    // its `form` attribute to the on-page Shopify product form.
    // Let the browser dispatch one trusted submit event; Horizon handles it
    // through on:submit=/handleSubmit (variant, quantity, drawer & errors).
    // The extra desktop click proxy in older themes prevented this action.
    if (window.matchMedia('(min-width: 750px)').matches) return;
    // Mobile keeps the click flow that already works on Olivia & Jack.
    event.preventDefault();
    this.handleAddToCartClick();
  };

  /**
   * Send the chosen product variant and quantity using Shopify's native
   * product form. Never create a second cart implementation or POST a stale
   * variant ID: the existing form already tracks color, size, and quantity.
   */
  handleAddToCartClick = () => {
    const stickyButton = this.querySelector('.sticky-add-to-cart__button');
    if (!(stickyButton instanceof HTMLButtonElement)) return;
    if (stickyButton.disabled || stickyButton.getAttribute('aria-busy') === 'true') return;
    if (this.dataset.variantAvailable === 'false') return;

    const productForm = this.#getProductForm();
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]')
      || productForm?.querySelector('form');
    const nativeButton = productForm?.querySelector('button[ref="addToCartButton"]')
      || form?.querySelector('button[type="submit"]');

    // If the section was just re-rendered, query again at click time.
    if (!(form instanceof HTMLFormElement)) {
      console.warn('[Olivia & Jack] Sticky ATC could not find the product form.');
      return;
    }
    const variantField = form.querySelector('input[name="id"]');
    if (!(variantField instanceof HTMLInputElement) || !variantField.value) {
      console.warn('[Olivia & Jack] No selectable product variant found.');
      return;
    }
    if (nativeButton instanceof HTMLButtonElement && nativeButton.disabled) return;

    // Tell the cart drawer that this product-form submission came specifically
    // from the mobile sticky CTA. Shopify's normal cart-update event (and header
    // count bubble) is still dispatched. A normal product-page ATC still opens it.
    productForm.dataset.ojStickyMobileAdd = 'true';

    // V10: Prevent the native Horizon cart-drawer auto-open for THIS sticky
    // add only. The existing Shopify form still adds the item, dispatches the
    // normal cartLinesUpdate event and updates the header cart count.
    // Keep auto-open off until the request settles; do not restore it
    // synchronously, as the drawer listens to the cart update promise.
    const cartDrawerComponent = document.querySelector('theme-drawer#cart-drawer cart-drawer-component');
    if (cartDrawerComponent?.hasAttribute('auto-open')) {
      cartDrawerComponent.removeAttribute('auto-open');
      this.ojRestoreCartAutoOpen = () => {
        const currentDrawer = document.querySelector('theme-drawer#cart-drawer cart-drawer-component');
        if (currentDrawer && !currentDrawer.hasAttribute('auto-open')) {
          currentDrawer.setAttribute('auto-open', '');
        }
        this.ojRestoreCartAutoOpen = null;
      };
    }
    this.#targetAddToCartButton = nativeButton instanceof HTMLButtonElement
      ? nativeButton : null;
    stickyButton.setAttribute('aria-busy', 'true');

    try {
      if (this.#targetAddToCartButton && this.#targetAddToCartButton.form === form) {
        this.#targetAddToCartButton.dataset.puppet = 'true';
        // The actual Shopify Add to Cart button triggers native validation,
        // quantity rules, form submission, cart events, and drawer opening.
        this.#targetAddToCartButton.click();
      } else if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        // Old browser fallback: submit event is still handled by Shopify.
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } catch (error) {
      delete productForm.dataset.ojStickyMobileAdd;
      this.ojRestoreCartAutoOpen?.();
      stickyButton.removeAttribute('aria-busy');
      this.#targetAddToCartButton?.removeAttribute('data-puppet');
      console.error('[Olivia & Jack] Sticky Add to Cart failed:', error);
      return;
    }

    // The cartLinesUpdate/cartError events clear this earlier in normal use.
    // Fail-safe in case extensions suppress those events.
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = window.setTimeout(() => {
      delete productForm.dataset.ojStickyMobileAdd;
      this.ojRestoreCartAutoOpen?.();
      stickyButton.removeAttribute('aria-busy');
      this.#targetAddToCartButton?.removeAttribute('data-puppet');
    }, 6500);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    const restoreStickyState = () => {
      this.ojRestoreCartAutoOpen?.();
      this.querySelector('.sticky-add-to-cart__button')?.removeAttribute('aria-busy');
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.removeAttribute('data-puppet');
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(() => { resetPuppet(); restoreStickyState(); });
    } else {
      resetPuppet();
      restoreStickyState();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    // Prefer the form in the same product section; otherwise fall back to the
    // page's own product form (some theme templates wrap it in another section).
    const sectionElement = this.closest('.shopify-section');
    const localForm = sectionElement?.querySelector(
      `product-form-component[data-product-id="${productId}"]`
    );
    return localForm || document.querySelector(
      `main product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
